import React, { useContext, useEffect, useMemo, useState } from 'react';
import Loader from '../../../../../Components/Loader';
import PageHeading from '../../../../../Components/PageHeading';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import { initialVideoToBannerSwitchInContentConfig } from '../../../../../utilities/initialConfigs';
import {
	getConfiguredIdsAsOptions,
	hasConfigKeys,
	shouldShowTgmrBackedConfig
} from '../../../../../utilities/tgmrSectionAdsHelpers';
import constants from '../constants.json';
import PlatformConfig from './PlatformConfig';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Paper } from '@mui/material';
import Tab from '@mui/material/Tab';
import useCommonStyles from '../../../../../commonStyles';

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: { RESPONSIVE, PLATFORM_DESKTOP, PLATFORM_MOBILE, PLATFORM_ALL, AP_BANNERSECTIONID }
} = constants;

const VBS_SUBTYPE = 'vbs';
const RESPONSIVE_PLATFORM_DESKTOP = String(PLATFORM_DESKTOP || '').toLowerCase();
const RESPONSIVE_PLATFORM_MOBILE = String(PLATFORM_MOBILE || '').toLowerCase();
const RESPONSIVE_PLATFORM_ALL = String(PLATFORM_ALL || '').toLowerCase();

const getVbsAdsAndSectionIds = (ads = [], supportedResponsivePlatforms = []) => {
	if (!ads?.length) {
		return { ads: [], sectionIds: [] };
	}

	const filteredVbsAds = ads.filter((adDoc) => {
		const subtype = adDoc?.formatData?.subType;

		const normalizedResponsivePlatform = String(
			adDoc?.formatData?.responsivePlatform || ''
		).toLowerCase();

		return (
			String(subtype || '').toLowerCase() === VBS_SUBTYPE &&
			adDoc?.formatData?.platform === RESPONSIVE &&
			supportedResponsivePlatforms.includes(normalizedResponsivePlatform)
		);
	});

	const ids = filteredVbsAds.map((adDoc) => adDoc?.id);
	const validIds = ids.filter(Boolean);
	const uniqueIds = [...new Set(validIds)];

	return {
		ads: filteredVbsAds,
		sectionIds: uniqueIds
	};
};

const VideoToBannerSwitchInContentConfig = ({ keyData, featureDescription }) => {
	const commonStyles = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { tgmrDoc, updateTgmrDoc } = useContext(UnitsContext);
	const [selectedPlatform, setSelectedPlatform] = useState(DESKTOP);
	const [errorMessage, setErrorMessage] = useState('');
	const [isDocLoading, setIsDocLoading] = useState(true);
	const currentVideoSectionId = rawConfig.videoSectionId;

	useEffect(() => {
		if (!tgmrDoc) {
			setIsDocLoading(true);
			updateTgmrDoc().finally(() => setIsDocLoading(false));
		} else {
			setIsDocLoading(false);
		}
	}, [tgmrDoc]);

	const vbsAdsForCurrentSection = useMemo(() => {
		const sectionAds = tgmrDoc?.ads?.filter(
			(ad) => ad?.framerateVbsSectionId === currentVideoSectionId
		);

		return sectionAds || [];
	}, [tgmrDoc, currentVideoSectionId]);

	const hasAds = vbsAdsForCurrentSection.length > 0;
	const hasFeatureConfig = hasConfigKeys(rawConfig, keyData);
	const shouldShowConfigUi = shouldShowTgmrBackedConfig({ hasAds, hasFeatureConfig });

	const desktopVbsAdsAndIds = useMemo(() => {
		return getVbsAdsAndSectionIds(vbsAdsForCurrentSection, [
			RESPONSIVE_PLATFORM_DESKTOP,
			RESPONSIVE_PLATFORM_ALL
		]);
	}, [vbsAdsForCurrentSection]);

	const mobileVbsAdsAndIds = useMemo(() => {
		return getVbsAdsAndSectionIds(vbsAdsForCurrentSection, [
			RESPONSIVE_PLATFORM_MOBILE,
			RESPONSIVE_PLATFORM_ALL
		]);
	}, [vbsAdsForCurrentSection]);

	const { ads: desktopVbsAds, sectionIds: desktopSectionIdsFromAds } = desktopVbsAdsAndIds;
	const { ads: mobileVbsAds, sectionIds: mobileSectionIdsFromAds } = mobileVbsAdsAndIds;

	const desktopSectionIdOptions = hasAds
		? desktopSectionIdsFromAds
		: getConfiguredIdsAsOptions([rawConfig[`${keyData}.${DESKTOP}.${AP_BANNERSECTIONID}`]]);
	const mobileSectionIdOptions = hasAds
		? mobileSectionIdsFromAds
		: getConfiguredIdsAsOptions([
				rawConfig[`${keyData}.${MOBILE}.${AP_BANNERSECTIONID}`],
				rawConfig[`${keyData}.${TABLET}.${AP_BANNERSECTIONID}`]
			]);


	useEffect(() => {
		let updatedRawConfig = structuredClone(rawConfig);

		if (hasAds && !hasFeatureConfig) {
			updatedRawConfig = { ...updatedRawConfig, ...initialVideoToBannerSwitchInContentConfig };
		}
		applyDefaultBannerSectionIds(updatedRawConfig);
		setErrorMessage(!shouldShowConfigUi ? 'Please create an ApTag to enable this feature' : '');
	}, [tgmrDoc, currentVideoSectionId, hasAds, hasFeatureConfig, shouldShowConfigUi]);

	const defaultDesktopVbsAd = useMemo(() => {
		if (!desktopVbsAds?.length) {
			return {};
		}
		return desktopVbsAds[0] || {};
	}, [desktopVbsAds]);

	const defaultMobileVbsAd = useMemo(() => {
		if (!mobileVbsAds?.length) {
			return {};
		}
		return mobileVbsAds[0] || {};
	}, [mobileVbsAds]);

	const applyDefaultBannerSectionIds = (configSnapshot) => {
		const desktopKey = `${keyData}.${DESKTOP}.${AP_BANNERSECTIONID}`;
		const mobileKey = `${keyData}.${MOBILE}.${AP_BANNERSECTIONID}`;
		const tabletKey = `${keyData}.${TABLET}.${AP_BANNERSECTIONID}`;

		if (!configSnapshot[desktopKey] && defaultDesktopVbsAd?.id) {
			configSnapshot[desktopKey] = defaultDesktopVbsAd.id;
		}

		if (!configSnapshot[mobileKey] && defaultMobileVbsAd?.id) {
			configSnapshot[mobileKey] = defaultMobileVbsAd.id;
		}

		if (!configSnapshot[tabletKey] && defaultMobileVbsAd?.id) {
			configSnapshot[tabletKey] = defaultMobileVbsAd.id;
		}

		updateRawConfig(configSnapshot);
	};

	const handlePlatformChange = (event, newValue) => {
		setSelectedPlatform(newValue);
	};

	return (
		<Paper className={commonStyles.mainPagePaperMarginStyle}>
			{isDocLoading && <Loader label="Loading Video To Banner Switch Config..." />}

			{!isDocLoading && !shouldShowConfigUi && (
				<Box
					sx={{
						marginTop: '4rem',
						paddingBottom: '15rem',
						textAlign: 'center',
						color: 'red',
						fontWeight: 'bold',
						fontSize: '2rem'
					}}
				>
					{errorMessage || 'Please create an ApTag to enable this feature'}
				</Box>
			)}

			{!isDocLoading && shouldShowConfigUi && (
				<>
					<PageHeading
						headingName="Video to Banner Switch In Content"
						headingPath={[
							'Dashboard',
							'Framerate',
							'Features Data',
							'Video to Banner Switch In Content'
						]}
						description="Replace video player with banner when player starts to float"
						showInfoButton={true}
						infoMessage={featureDescription}
					/>

					<TabContext value={selectedPlatform}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center'
							}}
						>
							<TabList onChange={handlePlatformChange}>
								<Tab label={DESKTOP} value={DESKTOP} />
								<Tab label={MOBILE} value={MOBILE} />
								<Tab label={TABLET} value={TABLET} />
							</TabList>
						</Box>
						<TabPanel value={DESKTOP}>
							<PlatformConfig
								keyData={`${keyData}.${DESKTOP}`}
								sectionIdKey={AP_BANNERSECTIONID}
								label="Select Section ID"
								sectionIdList={desktopSectionIdOptions}
							/>
						</TabPanel>
						<TabPanel value={MOBILE}>
							<PlatformConfig
								keyData={`${keyData}.${MOBILE}`}
								sectionIdKey={AP_BANNERSECTIONID}
								label="Select Section ID"
								sectionIdList={mobileSectionIdOptions}
							/>
						</TabPanel>
						<TabPanel value={TABLET}>
							<PlatformConfig
								keyData={`${keyData}.${TABLET}`}
								sectionIdKey={AP_BANNERSECTIONID}
								label="Select Section ID"
								sectionIdList={mobileSectionIdOptions}
							/>
						</TabPanel>
					</TabContext>
				</>
			)}
		</Paper>
	);
};

export default VideoToBannerSwitchInContentConfig;
